import React from "react";
import { ChevronRight } from "lucide-react";
import { LinkAnchor } from "../../components/ui/link";
import { ContractorSeoLayout } from "./ContractorSeoLayout";
import { ContractorSeoCta } from "./ContractorSeoCta";
import type { ContractorSeoArticlePageProps } from "./types";

const Breadcrumbs: React.FC<{
  items: ContractorSeoArticlePageProps["breadcrumbs"];
}> = ({ items }) => (
  <nav aria-label="Breadcrumb" className="text-sm text-muted-foreground">
    <ol className="flex flex-wrap items-center gap-1">
      {items.map((item, idx) => {
        const isLast = idx === items.length - 1;
        return (
          <li key={`${item.url}-${idx}`} className="flex items-center gap-1">
            {isLast ? (
              <span aria-current="page" className="text-foreground">
                {item.name}
              </span>
            ) : (
              <LinkAnchor
                href={item.url}
                className="hover:text-accent hover:no-underline"
              >
                {item.name}
              </LinkAnchor>
            )}
            {!isLast ? (
              <ChevronRight className="h-3 w-3 text-muted-foreground" />
            ) : null}
          </li>
        );
      })}
    </ol>
  </nav>
);

export const ContractorSeoArticlePage: React.FC<
  ContractorSeoArticlePageProps
> = (props) => {
  const { h1, bodyHtml, faqEntries, breadcrumbs, business } = props;

  return (
    <ContractorSeoLayout {...props}>
      <article className="mx-auto max-w-3xl px-4 py-16 sm:px-6 lg:px-8">
        <Breadcrumbs items={breadcrumbs} />
        <h1 className="mt-4 font-display text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
          {h1}
        </h1>
        <div
          className="mt-8 space-y-4 text-base leading-relaxed text-foreground prose-seo"
          dangerouslySetInnerHTML={{ __html: bodyHtml }}
        />

        {faqEntries.length > 0 ? (
          <section className="mt-12 border-t border-border pt-10">
            <h2 className="font-display text-2xl font-semibold tracking-tight text-foreground">
              Frequently Asked Questions
            </h2>
            <dl className="mt-6 space-y-6">
              {faqEntries.map((entry, idx) => (
                <div key={`${entry.question}-${idx}`}>
                  <dt className="font-display text-base font-semibold text-foreground">
                    {entry.question}
                  </dt>
                  <dd className="mt-2 text-sm leading-relaxed text-muted-foreground">
                    {entry.answer}
                  </dd>
                </div>
              ))}
            </dl>
          </section>
        ) : null}

        <ContractorSeoCta business={business} />
      </article>
    </ContractorSeoLayout>
  );
};
